import { initializeApp } from "firebase/app";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  collection,
  deleteDoc,
  getDocs,
  addDoc,
  writeBatch,
  updateDoc,
  query,
  orderBy,
  limit,
  where,
  serverTimestamp,
  Timestamp,
  getCountFromServer,
} from "firebase/firestore";
import { getStorage, ref, getDownloadURL, uploadBytes } from "firebase/storage";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithCustomToken,
  updateEmail,
  updatePassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";

const firebaseProductionConfig = {
  apiKey: "AIzaSyAqeO06E_OXSWKMBNmBnIlD7g60fY1jYSY",
  authDomain: "jks-israel-app.firebaseapp.com",
  databaseURL:
    "https://jks-israel-app-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "jks-israel-app",
  storageBucket: "jks-israel-app.appspot.com",
  messagingSenderId: "265218182067",
  appId: "1:265218182067:web:762cc3b2e5c0a98ab519c6",
};

const app = initializeApp(firebaseProductionConfig);

// Export the app for use in other modules
export { app };

// Initialize Cloud Firestore and get a reference to the service
export const db = getFirestore(app);

// Init Auth
export const auth = getAuth(app);

// Images Storage Connection
const storage = getStorage(app);

// Initialize Cloud Functions
const functions = getFunctions(app, "us-central1");

export const getGroupData = async (ID, groupID) => {
  try {
    const groupRef = doc(db, "users", ID, "groups", groupID);

    const groupDoc = await getDoc(groupRef);

    if (!groupDoc.exists()) {
      return null;
    }

    const groupData = groupDoc.data();
    return groupData;
  } catch (error) {
    return null;
  }
};

export const submitNewStudentToGroup = async (
  ID,
  groupID,
  studentData,
  image
) => {
  let newStudentRef = doc(collection(db, "users", ID, "students"));

  const newId = newStudentRef.id;

  // Fetching the image from the device's cache
  const imageRef = await fetch(image);

  // Image Time Stamp
  const imageUploadTime = Date.now();

  // Creating a blob to send to the storage
  const blob = await imageRef.blob();

  // Setting a filename according to the student ID
  const filename = `${newId}${imageUploadTime}.jpg`;

  // Uploading the blob to the storage under the filename
  await uploadBytes(ref(storage, filename), blob);

  // getting the uploaded image URI from the storage
  const imageURI = await getStudentImageURI(newId, imageUploadTime);

  // sending the student's data to the DB
  return await setDoc(newStudentRef, {
    ...studentData,
    studentCreatedAt: Timestamp.fromDate(new Date()),
    id: newId,
    imageUploadTime: imageUploadTime,
    imageURI: imageURI,
    groupsIDs: [groupID],
    classPresenceRecords: [],
    status: "toReview",
  })
    .then((res) => {
      return true;
    })
    .catch((err) => {
      return false;
    });
};

// A function the fetches the imageURI of a student's image that is stored on the cloud
export const getStudentImageURI = async (studentID, timeUpload) => {
  return await getDownloadURL(ref(storage, `${studentID}${timeUpload}.jpg`))
    .then((imageURI) => {
      return imageURI;
    })
    .catch(() => {
      return "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png";
    });
};

//// Managers
// Function to sign in the manager
export const managerSignIn = async (email, password) => {
  try {
    const { user } = await signInWithEmailAndPassword(auth, email, password);

    // Run password update check in the background
    (async () => {
      try {
        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();

          if (userData.userPassword !== password) {
            await updateDoc(userDocRef, {
              userPassword: password,
              lastPasswordUpdate: new Date(),
            });
          } else {
          }
        } else {
        }
      } catch (error) {
        // Don't throw the error since this is running in background
      }
    })();

    return user;
  } catch (error) {
    console.error("[managerSignIn] Error during sign-in:", error.message);
    // Handle sign-in error
    throw error;
  }
};

// Helper function to generate a 6-character organization code
const generateOrganizationCode = () => {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < 6; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

// Function to get the owner ID of an organization using the organization code
export const getOrgOwnerData = async (organizationCode) => {
  try {
    if (!organizationCode) {
      throw new Error("Organization code is required");
    }

    const usersRef = collection(db, "users");
    const q = query(
      usersRef,
      where("userOrganization.organizationCode", "==", organizationCode),
      where("userType", "==", "manager"),
      limit(1)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    // Return the first (and should be only) document's data
    return querySnapshot.docs[0].data();
  } catch (error) {
    console.error("Error getting organization owner ID:", error);
    throw error;
  }
};

// Function to sign up a manager
/**
 * Signs up a new manager user with optional payment details
 *
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @param {string} userName - User's full name
 * @param {string} userOrganizationName - Organization name
 * @param {string} userPhoneNumber - User's phone number
 * @param {string} userField - User's field/domain (e.g., "martial_arts")
 * @param {string} selectedPackage - Selected subscription package (bronze/silver/gold)
 * @param {Object} [paymentDetails=null] - Payment details object with the following structure:
 *   - plan: string - Subscription plan (bronze/silver/gold)
 *   - didPay: boolean - Whether payment was completed
 *   - trialPeriodInMonths: number - Trial period duration
 *   - lastPaymentDate: Date|null - Last payment date
 *   - registrationDate: Date - Registration date
 *   - userSuspended: boolean - Whether user is suspended
 *   - token: string|null - Payment token from payment gateway
 * @returns {Promise<Object>} Firebase user object
 * @throws {Error} When signup fails
 */
export const managerSignUp = async (
  email,
  password,
  userName,
  userOrganizationName,
  userPhoneNumber,
  userField,
  selectedPackage,
  paymentDetails = null
) => {
  try {
    console.log("Manager sign up details received:", {
      email,
      password,
      userName,
      userOrganizationName,
      userPhoneNumber,
      userField,
      selectedPackage,
      paymentDetails,
    });

    // Create a new user with email and password
    const { user } = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    // Generate organization code
    const organizationCode = generateOrganizationCode();

    // Determine the plan from the selected package
    let plan = "bronze"; // default fallback
    if (typeof selectedPackage === "string") {
      plan = selectedPackage;
    } else if (selectedPackage && typeof selectedPackage === "object") {
      plan = selectedPackage.name || selectedPackage.plan || "bronze";
    }

    // Remove password from the data to be stored in Firestore
    const userData = {
      userName,
      userPhoneNumber,
      userPassword: password,
      userType: "manager",
      userStatus: "active",
      userField,
      userEmailAddress: email,
      lastPasswordUpdate: new Date(),
      lastUpdated: new Date(),
      userCreatedAt: new Date(),
      lastLoginAt: new Date(),
      userOrganizationName,
      userOrganization: {
        organizationCode,
        organizationOwnerID: user.uid,
      },
      paymentDetails: {
        plan: plan,
        didPay: false,
        trialPeriodInMonths: 3,
        lastPaymentDate: null,
        registrationDate: null,
        userSuspended: false,
        token: "",
        registrationDocId: "",
        lastBillingStatus: null,
        lastBillingAttemptDate: null,
      },
    };

    // Create a new document in Firestore under "users/{uid}"
    const userDocRef = doc(db, "users", user.uid);
    await setDoc(userDocRef, userData);

    // Update the userId field with the document ID
    await updateDoc(userDocRef, {
      userId: user.uid,
    });

    return user;
  } catch (error) {
    throw error;
  }
};

// Function to sign up a coach
export const coachSignUp = async (
  email,
  password,
  userName,
  organizationCode,
  userPhoneNumber
) => {
  try {
    // Create user with email and password
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    // Get organization owner ID
    const ownerData = await getOrgOwnerData(organizationCode);

    if (!ownerData) {
      throw new Error("ארגון לא נמצא");
    }

    const userData = {
      userName: userName,
      userField: ownerData.userField,
      userPhoneNumber: userPhoneNumber,
      userPassword: password,
      userType: "coach",
      userStatus: "waitingForOrgAccept",
      userEmailAddress: email,
      lastPasswordUpdate: new Date(),
      lastUpdated: new Date(),
      userCreatedAt: new Date(),
      lastLoginAt: new Date(),
      userOrganizationName: ownerData?.userOrganizationName,
      userOrganization: {
        organizationCode: organizationCode,
        organizationOwnerID: ownerData?.userId,
      },
      paymentDetails: {
        plan: ownerData?.paymentDetails?.plan || "bronze",
        didPay: ownerData?.paymentDetails?.didPay || false,
        trialPeriodInMonths:
          ownerData?.paymentDetails?.trialPeriodInMonths || 3,
        lastPaymentDate: ownerData?.paymentDetails?.lastPaymentDate || null,
        registrationDate:
          ownerData?.paymentDetails?.registrationDate || new Date(),
        userSuspended: ownerData?.paymentDetails?.userSuspended || false,
        token: ownerData?.paymentDetails?.token || "",
        registrationDocId: ownerData?.paymentDetails?.registrationDocId || "",
      },
    };

    // Create a new document in Firestore under "users/{uid}"
    const userDocRef = doc(db, "users", user.uid);
    await setDoc(userDocRef, userData);

    // Update the userId field with the document ID
    await updateDoc(userDocRef, {
      userId: user.uid,
    });

    // Add coach document to owner's coaches collection

    const ownerCoachesRef = collection(
      db,
      "users",
      ownerData.userId,
      "coaches"
    );

    const coachDocRef = doc(ownerCoachesRef, user.uid);

    const coachData = {
      coachUserID: user.uid,
      coachSubmitToOrgTime: new Date(),
      accessPrivilege: {
        groups: { view: true, write: true },
        events: { view: true, write: true },
        coaches: { view: true, write: true },
        students: { view: true, write: true },
        trainingPlan: { view: true, write: true },
      },
      coachStatus: "waitingForOrgAccept",
    };

    await setDoc(coachDocRef, coachData);

    return user;
  } catch (error) {
    throw error;
  }
};

export const managerLogout = async () => {
  await signOut(auth);
};

export const getUserData = async (uid) => {
  try {
    const docRef = doc(db, "users", uid);
    const unionDoc = await getDoc(docRef);
    if (unionDoc.exists) {
      const userData = unionDoc.data();

      // If user is a coach, fetch their coach document under the manager's document
      if (
        userData.userType === "coach" &&
        userData.userOrganization?.organizationOwnerID
      ) {
        const managerId = userData.userOrganization.organizationOwnerID;
        const coachDocRef = doc(db, "users", managerId, "coaches", uid);
        const coachDoc = await getDoc(coachDocRef);

        // Also fetch the manager's data to use their payment details
        const managerDocRef = doc(db, "users", managerId);
        const managerDoc = await getDoc(managerDocRef);

        // Default coach data with full access privileges
        const defaultCoachData = {
          coachUserID: uid,
          coachSubmitToOrgTime: new Date(),
          accessPrivilege: {
            groups: { view: true, write: true },
            events: { view: true, write: true },
            coaches: { view: true, write: true },
            students: { view: true, write: true },
            trainingPlan: { view: true, write: true },
          },
          coachStatus: "active",
        };

        const coachData = {
          ...userData,
          coachData: coachDoc.exists ? coachDoc.data() : defaultCoachData,
        };

        // If manager exists, use their payment details for the coach
        if (managerDoc.exists) {
          const managerData = managerDoc.data();
          coachData.paymentDetails = managerData.paymentDetails;
          coachData.userCreatedAt = managerData.userCreatedAt;
          coachData.managerData = managerData; // Store manager data for reference
        }

        return coachData;
      }
      return userData;
    } else {
      return {
        status: "user does not exist",
      };
    }
  } catch (error) {
    console.error("Error in getUserData:", error);
    return {
      status: "error",
      error: error.message,
    };
  }
};

// Syncing Data
export const syncUserEvents = async (uid, events) => {
  try {
    const eventsCollectionRef = collection(db, "users", uid, "events");
    const eventsQuerySnapshot = await getDocs(eventsCollectionRef);

    const batch = writeBatch(db);
    eventsQuerySnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });
    await batch.commit();

    const eventPromises = events.map(async (event) => {
      const eventDocRef = doc(collection(db, "users", uid, "events"));
      await setDoc(eventDocRef, { ...event, id: eventDocRef.id });
    });
    await Promise.all(eventPromises);
  } catch (error) { }
};

export const getUserEvents = async (uid) => {
  try {
    const eventsCollectionRef = collection(db, "users", uid, "events");
    const userEventsQuerySnapshot = await getDocs(eventsCollectionRef);

    const userEvents = userEventsQuerySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return userEvents;
  } catch (error) {
    console.error("Error getting user events:", error);
    return null;
  }
};

export const syncUserCoaches = async (uid, coaches) => {
  try {
    const coachesCollectionRef = collection(db, "users", uid, "coaches");
    const coachesQuerySnapshot = await getDocs(coachesCollectionRef);

    const batch = writeBatch(db);
    coachesQuerySnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });
    await batch.commit();

    const coachPromises = coaches.map(async (coach) => {
      const coachDocRef = doc(collection(db, "users", uid, "coaches"));
      await setDoc(coachDocRef, { ...coach, id: coachDocRef.id });
    });
    await Promise.all(coachPromises);
  } catch (error) { }
};

export const getUserCoaches = async (uid) => {
  try {
    const coachesCollectionRef = collection(db, "users", uid, "coaches");
    const userCoachesQuerySnapshot = await getDocs(coachesCollectionRef);

    const userCoaches = userCoachesQuerySnapshot.docs.map((doc) => ({
      id: doc.id, // Ensure the document ID is included
      ...doc.data(),
    }));

    return userCoaches;
  } catch (error) {
    console.error("Error getting user coaches:", error);
    return null;
  }
};

export const syncUserGroups = async (uid, groups) => {
  try {
    const groupsCollectionRef = collection(db, "users", uid, "groups");
    const groupsQuerySnapshot = await getDocs(groupsCollectionRef);

    const batch = writeBatch(db);
    groupsQuerySnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });
    await batch.commit();

    const groupPromises = groups.map(async (group) => {
      const groupDocRef = doc(collection(db, "users", uid, "groups"));
      await setDoc(groupDocRef, { ...group, id: groupDocRef.id });
    });
    await Promise.all(groupPromises);
  } catch (error) { }
};

export const getUserGroups = async (uid) => {
  try {
    const groupsCollectionRef = collection(db, "users", uid, "groups");
    const userGroupsQuerySnapshot = await getDocs(groupsCollectionRef);

    const userGroups = userGroupsQuerySnapshot.docs.map((doc) => ({
      id: doc.id, // Ensure the document ID is included
      ...doc.data(),
    }));

    return userGroups;
  } catch (error) {
    console.error("Error getting user groups:", error);
    return null;
  }
};

export const getUserStudents = async (uid) => {
  try {
    const studentsCollectionRef = collection(db, "users", uid, "students");
    const userStudentsQuery = query(studentsCollectionRef);
    const userStudentsQuerySnapshot = await getDocs(userStudentsQuery);

    const userStudents = userStudentsQuerySnapshot.docs.map((doc) => {
      const docData = doc.data();
      const actualDocId = doc.id;
      return {
        ...docData,
        id: actualDocId,
      };
    });

    return userStudents;
  } catch (error) {
    console.error("Error getting user students:", error);
    return null;
  }
};

// WRITE Functions
// These functions are performing specific WRITE actions on specific object types
export const deleteEvent = async (uid, eventID) => {
  const eventsCollectionRef = collection(db, "users", uid, "events");
  try {
    const eventDocRef = doc(eventsCollectionRef, eventID);
    await deleteDoc(eventDocRef);
    return eventID; // Return the deleted event ID
  } catch (error) {
    console.error("Error deleting event: ", error);
    throw error;
  }
};

export const writeNewEvent = async (uid, event) => {
  if (!uid || !event) {
    throw new Error("Missing required parameters: uid or event object");
  }

  try {
    const eventsCollectionRef = collection(db, "users", uid, "events");
    const docRef = await addDoc(eventsCollectionRef, event);

    await updateDoc(docRef, { id: docRef.id });

    const updatedEvent = { ...event, id: docRef.id };
    return updatedEvent;
  } catch (error) {
    console.error("Error adding event: ", error);
    throw error;
  }
};

// This function updates existing event
export const updateExistingEvent = async (uid, event) => {
  if (!uid || !event || !event.id) {
    throw new Error("Missing required parameters: uid or event.id");
  }

  try {
    const eventDocRef = doc(db, "users", uid, "events", event.id);
    await updateDoc(eventDocRef, event);
    return event; // Return the updated event object
  } catch (error) {
    console.error("Error updating event: ", error);
    throw error;
  }
};

// This function deletes group
export const deleteGroup = async (uid, groupID) => {
  const groupsCollectionRef = collection(db, "users", uid, "groups");
  try {
    const groupDocRef = doc(groupsCollectionRef, groupID);
    await deleteDoc(groupDocRef);
    return groupID; // Return the deleted group ID
  } catch (error) {
    console.error("Error deleting group: ", error);
    throw error;
  }
};

export const writeNewGroup = async (uid, group) => {
  if (!uid || !group) {
    throw new Error("Missing required parameters: uid or group object");
  }

  try {
    const groupsCollectionRef = collection(db, "users", uid, "groups");

    const docRef = await addDoc(groupsCollectionRef, group);
    await updateDoc(docRef, { id: docRef.id });

    const updatedGroup = { ...group, id: docRef.id };
    return updatedGroup;
  } catch (error) {
    console.error("Error adding group: ", error);
    throw error;
  }
};

export const updateExistingGroup = async (uid, group) => {
  if (!uid || !group || !group.id) {
    throw new Error("Missing required parameters: uid or group.id");
  }

  try {
    const groupDocRef = doc(db, "users", uid, "groups", group.id);
    await updateDoc(groupDocRef, group);
    return group; // Return the updated group object
  } catch (error) {
    console.error("Error updating group: ", error);
    throw error;
  }
};

export const signInWithCode = async (authCode) => {
  try {
    const response = await fetch(
      "https://us-central1-jks-israel-app.cloudfunctions.net/verifycodeandcreateuser",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code: authCode }), // Send the code in the request body
      }
    );
    const data = await response.json();

    if (response.ok) {
      // Get the custom token from the response
      const customToken = data.token;

      // Sign in with the custom token
      await signInWithCustomToken(auth, customToken);

      return data; // You can return the token or UID here
    } else {
      console.error("Error:", data.error);
      return null;
    }
  } catch (error) {
    console.error("Request failed:", error);
  }
};

// This function changes a student's status to "active" and thus accepting it
export const acceptNewStudent = async (uid, studentID) => {
  // Check if something is missing
  if (!uid || !studentID) {
    throw new Error("Missing required parameters: uid or studentID");
  }

  try {
    // Find the student's doc
    const studentRef = doc(db, "users", uid, "students", studentID);

    // Update the student's status to "active"
    await updateDoc(studentRef, { status: "active" });

    return true; // Report the success
  } catch (error) {
    console.error("Error accepting student:", error);
    throw error;
  }
};

// This function deletes a student
export const deleteStudent = async (uid, studentID) => {
  // Find the students collection
  const studentsCollectionRef = collection(db, "users", uid, "students");
  try {
    // Find the target student doc
    const studentDocRef = doc(studentsCollectionRef, studentID);
    // Perform the delete action
    await deleteDoc(studentDocRef);
    // log the status
    return true;
  } catch (error) {
    // Handle Error
    console.error("Error deleting student: ", error);
  }
  return false;
};

// This function is used to retrieve all the presence logs of an array of groups
export const getPresenceLogs = async (uid, groupsIDs) => {
  try {
    const allPresenceLogs = [];

    // Iterate over each groupID in the provided groupsIDs array
    for (const groupID of groupsIDs) {
      // Reference to the specific group document
      const groupRef = doc(db, "users", uid, "groups", groupID);

      // Reference to the "presenceLogs" sub-collection within the group
      const presenceLogsRef = collection(groupRef, "presence_logs");

      // Get the snapshot of the presence logs in the current group
      const presenceLogsSnapshot = await getDocs(presenceLogsRef);

      // Add the logs of this group to the combined array
      presenceLogsSnapshot.forEach((logDoc) => {
        allPresenceLogs.push(logDoc.data());
      });
    }

    // Return the combined array of presence logs from all groups
    return allPresenceLogs;
  } catch (error) {
    console.error("Error fetching presence logs: ", error);
    throw new Error("Failed to fetch presence logs");
  }
};

export const getProfessionalContent = async (uid) => {
  try {
    const professionalContentRef = collection(
      db,
      "users",
      uid,
      "professional_content"
    );
    const contentSnapshot = await getDocs(professionalContentRef);

    if (!contentSnapshot.empty) {
      // Get first document
      const firstDoc = contentSnapshot.docs[0];
      return firstDoc.data();
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error fetching professional content:", error);
    throw error;
  }
};

export const updateProfessionalContent = async (uid, contentData) => {
  try {
    // Get reference to the collection
    const collectionRef = collection(db, "users", uid, "professional_content");

    // Get all documents in the collection
    const querySnapshot = await getDocs(collectionRef);

    let docRef;

    // If no documents exist, create a new one
    if (querySnapshot.empty) {
      console.log("No existing document found, creating new one");
      docRef = doc(collectionRef);
      await setDoc(docRef, { years: { 0: {} } });
    } else {
      // Get the first document's ID
      const docId = querySnapshot.docs[0].id;
      console.log("Found document ID:", docId);
      docRef = doc(db, "users", uid, "professional_content", docId);
    }

    console.log("Using document reference:", docRef.path);

    // Ensure we're working with a plain JavaScript object
    // Remove any undefined values and convert to proper object structure
    const cleanData = {
      years: {
        0: Object.entries(contentData.years[0]).reduce(
          (acc, [year, yearData]) => {
            acc[year] = Object.entries(yearData).reduce(
              (monthAcc, [month, monthData]) => {
                monthAcc[month] = {
                  title: monthData.title || "",
                  description: monthData.description || "",
                  weeks: monthData.weeks
                    ? Object.entries(monthData.weeks).reduce(
                      (weekAcc, [week, weekData]) => {
                        weekAcc[week] = {
                          title: weekData.title || "",
                          description: weekData.description || "",
                        };
                        return weekAcc;
                      },
                      {}
                    )
                    : {},
                };
                return monthAcc;
              },
              {}
            );
            return acc;
          },
          {}
        ),
      },
    };

    console.log("Prepared clean data:", cleanData);

    // Update the document
    console.log("Attempting to update document...");
    await updateDoc(docRef, cleanData);
    console.log("Document successfully updated");

    return true;
  } catch (error) {
    console.log("Error details:", {
      errorName: error.name,
      errorMessage: error.message,
      errorStack: error.stack,
    });
    console.error("Error updating professional content:", error);
    throw error;
  }
};

// Function to update user details
export const updateUserDetails = async (
  uid,
  userDetails,
  updateAuthUser = false
) => {
  try {
    if (!uid) {
      throw new Error("User ID is required");
    }

    // Get reference to the user document
    const userRef = doc(db, "users", uid);

    // Update the user document with the new details
    await updateDoc(userRef, {
      userName: userDetails.name,
      userEmailAddress: userDetails.email,
      userPhoneNumber: userDetails.phone,
      userOrganizationName: userDetails.organizationName,
      lastUpdated: new Date(),
    });

    // If updateAuthUser is true and email has changed, update the auth user
    if (updateAuthUser && userDetails.email) {
      // Get the current user - auth.currentUser is a property, not a function
      const currentUser = auth.currentUser;

      if (currentUser && currentUser.uid === uid) {
        // Update the email in Firebase Authentication
        await updateEmail(currentUser, userDetails.email);
        console.log("Auth user email updated successfully");
      } else {
        console.warn(
          "Could not update auth user email: current user not found or doesn't match uid"
        );
      }
    }

    return { success: true };
  } catch (error) {
    console.error("Error updating user details:", error);
    throw error;
  }
};

// Function to update user password
export const updateUserPassword = async (uid, newPassword) => {
  try {
    if (!uid || !newPassword) {
      throw new Error("User ID and new password are required");
    }

    // Get the current user
    const currentUser = auth.currentUser;

    if (currentUser && currentUser.uid === uid) {
      // Update the password in Firebase Authentication
      await updatePassword(currentUser, newPassword);
      console.log("User password updated successfully");

      // Optionally, you can also update the user's document in Firestore if needed
      const userRef = doc(db, "users", uid);
      await updateDoc(userRef, {
        userPassword: newPassword,
        lastPasswordUpdate: new Date(), // Track when the password was last updated
      });

      return { success: true };
    } else {
      throw new Error("Current user not found or doesn't match uid");
    }
  } catch (error) {
    console.error("Error updating user password:", error);
    throw error;
  }
};

export const getStatsReports = async (uid) => {
  try {
    const statsReportsRef = collection(db, "users", uid, "stat_reports");
    const q = query(statsReportsRef, orderBy("timestamp", "desc"), limit(12));
    const querySnapshot = await getDocs(q);

    const reports = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return reports;
  } catch (error) {
    console.error("Error fetching stats reports:", error);
    throw error;
  }
};

export const getCoaches = async (uid) => {
  try {
    const coachesCollectionRef = collection(db, "users", uid, "coaches");
    const coachesQuerySnapshot = await getDocs(coachesCollectionRef);

    const coaches = await Promise.all(
      coachesQuerySnapshot.docs.map(async (coachDoc) => {
        const coachData = {
          id: coachDoc.id,
          ...coachDoc.data(),
        };

        // Fetch the coach's user data
        const userDocRef = doc(db, "users", coachData.coachUserID);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          return {
            ...coachData,
            userData: userDoc.data(),
          };
        }

        return coachData;
      })
    );

    return coaches;
  } catch (error) {
    console.error("[getCoaches] Error getting coaches:", error);
    return null;
  }
};

// This function migrates the user object to the user object model V2 - 14/04/2025
export const userObjectMigrate = async (uid) => {
  try {
    if (!uid) {
      throw new Error("User ID is required");
    }

    // Get reference to the user document
    const userRef = doc(db, "users", uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error("User document not found");
    }

    const userData = userDoc.data();
    const updates = {};
    const now = new Date();

    // Check and set default values for required fields
    if (!userData.userField) {
      updates.userField = "martial_arts";
    }
    if (
      !userData.userType ||
      (userData.userType !== "coach" && userData.userType !== "manager")
    ) {
      updates.userType = "manager";
    }
    if (!userData.userStatus) {
      updates.userStatus = "active";
    }
    if (!userData.lastPasswordUpdate) {
      updates.lastPasswordUpdate = now;
    }
    if (!userData.lastUpdated) {
      updates.lastUpdated = now;
    }
    if (!userData.userCreatedAt) {
      updates.userCreatedAt = now;
    }
    if (!userData.userOrganizationName) {
      updates.userOrganizationName = "";
    }

    // Handle payment details
    if (!userData.paymentDetails) {
      updates.paymentDetails = {
        plan: "bronze",
        didPay: false,
        lastPaymentDate: "",
        trialPeriodInMonths: 3,
        registrationDate: now,
      };
    } else if (
      userData.userType === "coach" &&
      userData.userOrganization?.organizationOwnerID
    ) {
      // If user is a coach, fetch owner's payment details
      const ownerId = userData.userOrganization.organizationOwnerID;
      const ownerRef = doc(db, "users", ownerId);
      const ownerDoc = await getDoc(ownerRef);

      if (ownerDoc.exists()) {
        const ownerData = ownerDoc.data();
        if (ownerData.paymentDetails) {
          // Compare and update if different
          const currentPaymentDetails = userData.paymentDetails;
          const ownerPaymentDetails = ownerData.paymentDetails;

          if (
            currentPaymentDetails.plan !== ownerPaymentDetails.plan ||
            currentPaymentDetails.didPay !== ownerPaymentDetails.didPay ||
            currentPaymentDetails.trialPeriodInMonths !==
            ownerPaymentDetails.trialPeriodInMonths ||
            currentPaymentDetails.lastPaymentDate !==
            ownerPaymentDetails.lastPaymentDate ||
            currentPaymentDetails.registrationDate !==
            ownerPaymentDetails.registrationDate
          ) {
            updates.paymentDetails = {
              plan: ownerPaymentDetails.plan,
              didPay: ownerPaymentDetails.didPay,
              trialPeriodInMonths: ownerPaymentDetails.trialPeriodInMonths,
              lastPaymentDate: ownerPaymentDetails.lastPaymentDate,
              registrationDate: ownerPaymentDetails.registrationDate,
            };
          }
        }
      }
    }

    updates.lastLoginAt = now;

    if (!userData.userOrganization) {
      updates.userOrganization = {
        organizationCode: generateOrganizationCode(),
        organizationOwnerID: uid,
      };
    }

    // Migrate coach access privileges
    if (userData.userType === "manager" || updates.userType === "manager") {
      const coachesRef = collection(db, "users", uid, "coaches");
      const coachesSnapshot = await getDocs(coachesRef);

      const batch = writeBatch(db);
      let hasUpdates = false;

      coachesSnapshot.forEach((coachDoc) => {
        const coachData = coachDoc.data();
        if (
          typeof coachData.accessPrivilege === "string" ||
          !coachData.accessPrivilege
        ) {
          batch.update(coachDoc.ref, {
            accessPrivilege: {
              groups: { view: true, write: true },
              events: { view: true, write: true },
              coaches: { view: true, write: true },
              students: { view: true, write: true },
              trainingPlan: { view: true, write: true },
            },
          });
          hasUpdates = true;
        }
      });

      if (hasUpdates) {
        await batch.commit();
        console.log("Coach access privileges migrated successfully");
      }
    }

    // Only update if there are changes needed
    if (Object.keys(updates).length > 0) {
      await updateDoc(userRef, updates);
      console.log("User object migrated successfully");
      return { ...userData, ...updates };
    }

    return userData;
  } catch (error) {
    console.error("Error in user object migration:", error);
    throw error;
  }
};

export const approveCoach = async (coachId, ownerId) => {
  try {
    // Update the coach's user document
    const coachRef = doc(db, "users", coachId);
    await updateDoc(coachRef, {
      userStatus: "active",
      lastUpdated: new Date(),
    });

    // Update the coach's status in the owner's coaches collection
    const ownerCoachesRef = doc(db, "users", ownerId, "coaches", coachId);
    await updateDoc(ownerCoachesRef, {
      coachStatus: "active",
    });

    return true;
  } catch (error) {
    console.error("Error approving coach:", error);
    throw error;
  }
};

export const enableCoach = async (coachId, ownerId) => {
  try {
    // Update the coach's user document
    const coachRef = doc(db, "users", coachId);
    await updateDoc(coachRef, {
      userStatus: "active",
      lastUpdated: new Date(),
    });

    // Update the coach's status in the owner's coaches collection
    const ownerCoachesRef = doc(db, "users", ownerId, "coaches", coachId);
    await updateDoc(ownerCoachesRef, {
      coachStatus: "active",
    });

    return true;
  } catch (error) {
    console.error("Error enabling coach:", error);
    throw error;
  }
};

export const disableCoach = async (coachId, ownerId) => {
  try {
    // Update the coach's user document
    const coachRef = doc(db, "users", coachId);
    await updateDoc(coachRef, {
      userStatus: "disabled",
      lastUpdated: new Date(),
    });

    // Update the coach's status in the owner's coaches collection
    const ownerCoachesRef = doc(db, "users", ownerId, "coaches", coachId);
    await updateDoc(ownerCoachesRef, {
      coachStatus: "disabled",
    });

    return true;
  } catch (error) {
    console.error("Error disabling coach:", error);
    throw error;
  }
};

export const rejectCoach = async (ownerId, coachId) => {
  try {
    // Delete the coach from the owner's coaches collection
    const ownerCoachesRef = doc(db, "users", ownerId, "coaches", coachId);
    await updateDoc(ownerCoachesRef, {
      userStatus: "disabled",
      lastUpdated: new Date(),
    });

    // Update the coach's user document to set status as disabled
    const coachRef = doc(db, "users", coachId);
    await updateDoc(coachRef, {
      userStatus: "disabled",
      lastUpdated: new Date(),
    });

    return true;
  } catch (error) {
    console.error("Error rejecting coach:", error);
    throw error;
  }
};

export const updateCoachPrivileges = async (ownerId, coachId, privileges) => {
  try {
    // Update the coach's privileges in the owner's coaches collection
    const ownerCoachesRef = doc(db, "users", ownerId, "coaches", coachId);
    await updateDoc(ownerCoachesRef, {
      accessPrivilege: privileges,
      lastUpdated: new Date(),
    });

    return true;
  } catch (error) {
    console.error("Error updating coach privileges:", error);
    throw error;
  }
};

export const sendSMS = async (phoneNumber, message) => {
  if (!auth.currentUser) {
    throw new Error("No authenticated user found");
  }

  if (!phoneNumber || !message) {
    throw new Error("Phone number and message are required");
  }

  try {
    const sendBulkSMS = httpsCallable(functions, "sendBulkSMS");

    console.log("Sending SMS with data:", {
      recipients: [phoneNumber],
      message,
    });

    const response = await sendBulkSMS({
      recipients: [phoneNumber],
      message: message,
    });

    console.log("SMS response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error sending SMS:", error);

    if (error.message.includes("auth")) {
      throw new Error("Authentication error - Please sign in again");
    } else if (error.message.includes("invalid")) {
      throw new Error(error.message || "Invalid arguments provided");
    } else {
      throw error;
    }
  }
};

export const submitUserFeedback = async (userId, feedback) => {
  try {
    if (!userId || !feedback) {
      throw new Error("User ID and feedback are required");
    }

    const feedbackRef = collection(
      db,
      "metadata",
      "feedback",
      "user_feedback_reports"
    );
    const feedbackData = {
      userId,
      satisfactionScore: feedback.satisfaction,
      whatUserLikes: feedback.likes,
      whatToImprove: feedback.improvements,
      feedbackTimestamp: new Date(),
      wasRead: false,
      whenWasRead: null,
    };

    await addDoc(feedbackRef, feedbackData);
    return true;
  } catch (error) {
    console.error("Error submitting user feedback:", error);
    throw error;
  }
};

// Save an evaluation template under users/{userId}/evaluationTemplates
export const submitEvaluationTemplate = async (userId, templateData) => {
  if (!userId || !templateData)
    throw new Error("Missing userId or templateData");
  try {
    const templatesCollection = collection(
      db,
      "users",
      userId,
      "evaluationTemplates"
    );
    // Add the document to get an auto-generated ID
    const docRef = await addDoc(templatesCollection, templateData);
    // Update the document to include templateId
    await updateDoc(docRef, {
      templateId: docRef.id,
      isArchived: false,
      isDefaultTemplate: false,
    });
    return { success: true, templateId: docRef.id };
  } catch (error) {
    console.error("Error submitting evaluation template:", error);
    throw error;
  }
};

// Fetch all evaluation templates for a user
export const getEvaluationTemplates = async (userId) => {
  if (!userId) throw new Error("Missing userId");
  try {
    const templatesCollection = collection(
      db,
      "users",
      userId,
      "evaluationTemplates"
    );
    const snapshot = await getDocs(templatesCollection);
    return snapshot.docs.map((doc) => doc.data());
  } catch (error) {
    console.error("Error fetching evaluation templates:", error);
    throw error;
  }
};

// Fetch evaluation reports for a specific event
export const getEventEvaluationReports = async (userId, eventId) => {
  if (!userId || !eventId) throw new Error("Missing userId or eventId");
  try {
    const reportsCollection = collection(
      db,
      "users",
      userId,
      "events",
      eventId,
      "evaluationReports"
    );
    const snapshot = await getDocs(reportsCollection);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error fetching evaluation reports:", error);
    throw error;
  }
};

// Update the default template status
export const updateDefaultTemplate = async (userId, templateId) => {
  if (!userId || !templateId) throw new Error("Missing userId or templateId");
  try {
    const templatesCollection = collection(
      db,
      "users",
      userId,
      "evaluationTemplates"
    );
    const snapshot = await getDocs(templatesCollection);

    // First, set all templates to not default
    const batch = writeBatch(db);
    snapshot.docs.forEach((doc) => {
      batch.update(doc.ref, { isDefaultTemplate: false });
    });

    // Then set the selected template as default
    const templateRef = doc(templatesCollection, templateId);
    batch.update(templateRef, { isDefaultTemplate: true });

    await batch.commit();
    return { success: true };
  } catch (error) {
    console.error("Error updating default template:", error);
    throw error;
  }
};

// Update template archive status
export const updateTemplateArchiveStatus = async (
  userId,
  templateId,
  isArchived
) => {
  if (!userId || !templateId) throw new Error("Missing userId or templateId");
  try {
    const templateRef = doc(
      db,
      "users",
      userId,
      "evaluationTemplates",
      templateId
    );
    console.log(
      "[updateTemplateArchiveStatus] userId:",
      userId,
      "templateId:",
      templateId,
      "isArchived:",
      isArchived
    );
    console.log(
      "[updateTemplateArchiveStatus] Firestore doc path:",
      templateRef.path
    );
    await updateDoc(templateRef, { isArchived: isArchived });
    console.log("[updateTemplateArchiveStatus] Update successful");
    return true;
  } catch (error) {
    console.error("Error updating template archive status:", error);
    return false;
  }
};

// This function changes a student's status to "active" and thus reactivating it
export const reactivateStudent = async (uid, studentID) => {
  // Check if something is missing
  if (!uid || !studentID) {
    throw new Error("Missing required parameters: uid or studentID");
  }

  try {
    // Find the student's doc
    const studentRef = doc(db, "users", uid, "students", studentID);

    // Update the student's status to "active"
    await updateDoc(studentRef, { status: "active" });

    return true; // Report the success
  } catch (error) {
    console.error("Error reactivating student:", error);
    throw error;
  }
};

// Update a student document in Firestore
export const updateStudent = async (uid, studentId, updatedData) => {
  if (!uid || !studentId || !updatedData) {
    throw new Error(
      "Missing required parameters: uid, studentId, or updatedData"
    );
  }
  try {
    const studentRef = doc(db, "users", uid, "students", studentId);
    const dataToUpdate = { ...updatedData };

    // Convert birthday to Firestore Timestamp if needed
    if (dataToUpdate.birthday) {
      if (typeof dataToUpdate.birthday === "string") {
        dataToUpdate.birthday = Timestamp.fromDate(
          new Date(dataToUpdate.birthday)
        );
      } else if (dataToUpdate.birthday instanceof Date) {
        dataToUpdate.birthday = Timestamp.fromDate(dataToUpdate.birthday);
      } else if (
        typeof dataToUpdate.birthday === "object" &&
        dataToUpdate.birthday.seconds
      ) {
        // Convert Firestore plain object to Timestamp
        dataToUpdate.birthday = new Timestamp(
          dataToUpdate.birthday.seconds,
          dataToUpdate.birthday.nanoseconds || 0
        );
      }
    }

    // Handle image upload if a new image is provided (local blob URL)
    if (dataToUpdate.imageURI && dataToUpdate.imageURI.startsWith("blob:")) {
      const response = await fetch(dataToUpdate.imageURI);
      const blob = await response.blob();
      const imageUploadTime = Date.now();
      const filename = `${studentId}${imageUploadTime}.jpg`;
      const storage = getStorage();
      await uploadBytes(ref(storage, filename), blob);
      const imageURI = await getStudentImageURI(studentId, imageUploadTime);
      dataToUpdate.imageURI = imageURI;
      dataToUpdate.imageUploadTime = imageUploadTime;
    }

    await updateDoc(studentRef, dataToUpdate);
    return true;
  } catch (error) {
    console.error("Error updating student:", error);
    throw error;
  }
};

// Efficiently count athlete_entered_portal actions in tech_logs for the past 24 hours
export const getAthletePortalEntriesTodayCount = async (uid) => {
  try {
    const now = new Date();
    // Create timestamp for 24 hours ago
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const q = query(
      collection(db, "users", uid, "tech_logs"),
      where("action", "==", "athlete_entered_portal"),
      where("actorType", "==", "student"),
      where("timestamp", ">=", Timestamp.fromDate(twentyFourHoursAgo))
    );
    const snapshot = await getCountFromServer(q);
    const count = snapshot.data().count;
    return count;
  } catch (error) {
    console.error(
      "Error counting past 24 hours athlete portal entries:",
      error
    );
    // Return 0 instead of throwing to prevent UI crashes
    return 0;
  }
};

// Efficiently count athlete_entered_portal actions in tech_logs for the past 168 hours (7 days)
export const getAthletePortalEntriesWeekCount = async (uid) => {
  try {
    const now = new Date();
    // Create timestamp for 168 hours (7 days) ago
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const q = query(
      collection(db, "users", uid, "tech_logs"),
      where("action", "==", "athlete_entered_portal"),
      where("actorType", "==", "student"),
      where("timestamp", ">=", Timestamp.fromDate(sevenDaysAgo))
    );
    const snapshot = await getCountFromServer(q);
    const count = snapshot.data().count;
    return typeof count === "number" ? count : 0;
  } catch (error) {
    console.error("Error counting past 7 days athlete portal entries:", error);
    // Return 0 instead of throwing to prevent UI crashes
    return 0;
  }
};

// Fetch the 20 latest portal activity logs for a user with specific actions
export const getLatestPortalActivityLogs = async (uid) => {
  try {
    const actions = [
      "athlete_entered_portal",
      "athlete_submit_new_eval_report",
      "athlete_update_existing_eval_report",
      "athlete_update_personal_details",
    ];

    // Firestore does not support 'in' with more than 10 values, but we have only 4
    const q = query(
      collection(db, "users", uid, "tech_logs"),
      where("action", "in", actions),
      orderBy("timestamp", "desc"),
      limit(20)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching latest portal activity logs:", error);
    // Return empty array instead of throwing to prevent UI crashes
    return [];
  }
};

// Submit a global technical log to the metadata collection
export const submitGlobalTechLog = async (logData) => {
  try {
    // Validate required fields
    if (
      !logData.action ||
      !logData.actorId ||
      !logData.actorType ||
      !logData.targetId ||
      !logData.targetType
    ) {
      throw new Error(
        "Missing required fields: action, actorId, actorType, targetId, targetType"
      );
    }

    // Validate actorType
    const validActorTypes = ["coach", "student", "manager"];
    if (!validActorTypes.includes(logData.actorType)) {
      throw new Error(
        "Invalid actorType. Must be one of: coach, student, manager"
      );
    }

    // Validate targetType
    const validTargetTypes = ["group", "student", "event", "-"];
    if (!validTargetTypes.includes(logData.targetType)) {
      throw new Error(
        "Invalid targetType. Must be one of: group, student, event"
      );
    }

    // Prepare the log document
    const logDocument = {
      timestamp: serverTimestamp(),
      action: logData.action,
      actorId: logData.actorId,
      actorType: logData.actorType,
      targetId: logData.targetId,
      targetType: logData.targetType,
      ...(logData.context && { context: logData.context }),
      ...(logData.metadata && { metadata: logData.metadata }),
    };

    // Add document to the global tech logs collection
    const globalLogsRef = collection(
      db,
      "metadata",
      "global_tech_logs",
      "logs"
    );
    const docRef = await addDoc(globalLogsRef, logDocument);

    return { success: true, logId: docRef.id };
  } catch (error) {
    console.error("Error submitting global tech log:", error);
    throw error;
  }
};

/**
 * Updates user payment details after successful payment registration
 *
 * @param {string} userId - User ID to update
 * @param {Object} paymentDetails - Updated payment details object
 * @returns {Promise<Object>} Success status and updated user data
 * @throws {Error} When update fails
 */
export const updateUserPaymentDetails = async (userId, paymentDetails) => {
  try {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!paymentDetails || typeof paymentDetails !== "object") {
      throw new Error("Payment details object is required");
    }

    // Validate required payment detail fields
    const requiredFields = [
      "plan",
      "didPay",
      "trialPeriodInMonths",
      "lastPaymentDate",
      "registrationDate",
      "userSuspended",
      "token",
      "registrationDocId",
    ];

    for (const field of requiredFields) {
      if (!(field in paymentDetails)) {
        throw new Error(`Missing required field: ${field}`);
      }
    }

    // Get reference to the user document
    const userRef = doc(db, "users", userId);

    // Check if user document exists
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new Error("User document not found");
    }

    // Prepare update data
    const updateData = {
      "paymentDetails.plan": paymentDetails.plan,
      "paymentDetails.didPay": paymentDetails.didPay,
      "paymentDetails.trialPeriodInMonths": paymentDetails.trialPeriodInMonths,
      "paymentDetails.lastPaymentDate": paymentDetails.lastPaymentDate,
      "paymentDetails.registrationDate": paymentDetails.registrationDate,
      "paymentDetails.userSuspended": paymentDetails.userSuspended,
      "paymentDetails.token": paymentDetails.token,
      "paymentDetails.registrationDocId": paymentDetails.registrationDocId,
      "paymentDetails.lastBillingStatus": paymentDetails.lastBillingStatus,
      lastUpdated: serverTimestamp(),
    };

    // Update the user document
    await updateDoc(userRef, updateData);

    console.log("User payment details updated successfully for user:", userId);

    // Get the updated user data
    const updatedUserDoc = await getDoc(userRef);
    const updatedUserData = updatedUserDoc.data();

    return {
      success: true,
      message: "Payment details updated successfully",
      userData: updatedUserData,
    };
  } catch (error) {
    console.error("Error updating user payment details:", error);
    throw new Error(`Failed to update payment details: ${error.message}`);
  }
};

/**
 * Suspends a user account by setting userSuspended to true
 *
 * @param {string} userId - User ID to suspend
 * @returns {Promise<Object>} Success status and updated user data
 * @throws {Error} When suspension fails
 */
export const suspendUserAccount = async (userId) => {
  try {
    if (!userId) {
      throw new Error("User ID is required");
    }

    // Get reference to the user document
    const userRef = doc(db, "users", userId);

    // Check if user document exists
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new Error("User document not found");
    }

    const userData = userDoc.data();

    // Check if user is already suspended
    if (userData.paymentDetails?.userSuspended) {
      throw new Error("User account is already suspended");
    }

    // Prepare update data
    const updateData = {
      "paymentDetails.userSuspended": true,
      "paymentDetails.suspendedAt": serverTimestamp(),
      lastUpdated: serverTimestamp(),
    };

    // Update the user document
    await updateDoc(userRef, updateData);

    console.log("User account suspended successfully for user:", userId);

    // Get the updated user data
    const updatedUserDoc = await getDoc(userRef);
    const updatedUserData = updatedUserDoc.data();

    return {
      success: true,
      message: "Account suspended successfully",
      userData: updatedUserData,
    };
  } catch (error) {
    console.error("Error suspending user account:", error);
    throw new Error(`Failed to suspend account: ${error.message}`);
  }
};

/**
 * Changes a user's package plan by updating the paymentDetails.plan field
 *
 * @param {string} userId - User ID to update
 * @param {string} newPackage - New package name (gold, silver, bronze)
 * @returns {Promise<Object>} Success status and updated user data
 * @throws {Error} When package change fails
 */
export const changePackagePlan = async (userId, newPackage) => {
  try {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!newPackage) {
      throw new Error("New package is required");
    }

    // Validate package name
    const validPackages = ["gold", "silver", "bronze"];
    const packageName = newPackage.toLowerCase();

    if (!validPackages.includes(packageName)) {
      throw new Error("Invalid package. Must be 'gold', 'silver', or 'bronze'");
    }

    // Get reference to the user document
    const userRef = doc(db, "users", userId);

    // Check if user document exists
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new Error("User document not found");
    }

    const userData = userDoc.data();
    const currentPlan =
      userData.paymentDetails?.plan?.toLowerCase() || "bronze";

    // Check if user is trying to change to the same plan
    if (currentPlan === packageName) {
      throw new Error("User is already on the selected package");
    }

    // Update the user's payment details
    const updateData = {
      "paymentDetails.plan": packageName,
      "paymentDetails.lastPackageChange": new Date(),
      lastUpdated: serverTimestamp(),
    };

    await updateDoc(userRef, updateData);

    console.log("Package changed successfully:", {
      userId,
      oldPackage: currentPlan,
      newPackage: packageName,
      timestamp: new Date(),
    });

    return {
      success: true,
      message: `Package successfully changed from ${currentPlan} to ${packageName}`,
      data: {
        oldPackage: currentPlan,
        newPackage: packageName,
        changeTimestamp: new Date(),
      },
    };
  } catch (error) {
    console.error("Error changing package plan:", error);
    throw new Error(`Package change failed: ${error.message}`);
  }
};

/**
 * Sends a password reset email to the specified email address
 *
 * @param {string} email - Email address to send password reset to
 * @returns {Promise<Object>} Success status
 * @throws {Error} When password reset email fails
 */
export const resetUserPassword = async (email) => {
  try {
    if (!email) {
      throw new Error("Email address is required");
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error("Please enter a valid email address");
    }

    // Send password reset email
    await sendPasswordResetEmail(auth, email);

    console.log("Password reset email sent successfully to:", email);

    return {
      success: true,
      message: "Password reset email sent successfully. Please check your inbox.",
    };
  } catch (error) {
    console.error("Error sending password reset email:", error);

    // Handle specific Firebase Auth errors
    if (error.code === "auth/user-not-found") {
      throw new Error("No account found with this email address");
    } else if (error.code === "auth/invalid-email") {
      throw new Error("Please enter a valid email address");
    } else if (error.code === "auth/too-many-requests") {
      throw new Error("Too many password reset attempts. Please try again later");
    } else {
      throw new Error(`Failed to send password reset email: ${error.message}`);
    }
  }
};
